import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Search, Users } from 'lucide-react';
import MarketingNavbar from '@/components/marketing/MarketingNavbar';
import { useVenuesList, type VenueSummary } from '@/api/hooks/useVenues';

const ALL = 'ALL';

/**
 * Public venue marketplace. Fetches every listable venue once and filters
 * client-side — works for MVP's handful of venues and keeps filter toggles
 * instant. Server-side search / pagination is a Phase 2 concern when
 * venue count justifies the complexity.
 *
 * <p>Uses {@link MarketingNavbar} rather than {@link CustomerLayout} because
 * this is a discovery surface — operators evaluating the platform may land
 * here via Google, and the "Book a demo" CTA is still relevant for them.
 */
export default function VenuesIndexPage() {
  const { data: allVenues, isLoading, isError } = useVenuesList();
  const [sportFilter, setSportFilter] = useState<string>(ALL);
  const [cityFilter, setCityFilter] = useState<string>(ALL);
  const [searchQuery, setSearchQuery] = useState('');

  const sportOptions = useMemo(() => {
    const seen = new Map<string, { code: string; name: string; icon: string | null }>();
    (allVenues ?? []).forEach((v) =>
      v.sports.forEach((s) => {
        if (!seen.has(s.sportCode)) {
          seen.set(s.sportCode, {
            code: s.sportCode,
            name: s.displayName,
            icon: s.icon,
          });
        }
      }),
    );
    return [{ code: ALL, name: 'All sports', icon: null as string | null }, ...seen.values()];
  }, [allVenues]);

  const cityOptions = useMemo(() => {
    const cities = new Set<string>();
    (allVenues ?? []).forEach((v) => v.city && cities.add(v.city));
    return [ALL, ...Array.from(cities).sort()];
  }, [allVenues]);

  const filteredVenues = useMemo(() => {
    if (!allVenues) return [];
    const q = searchQuery.trim().toLowerCase();
    return allVenues
      .filter((v) => sportFilter === ALL || v.sports.some((s) => s.sportCode === sportFilter))
      .filter((v) => cityFilter === ALL || v.city === cityFilter)
      .filter((v) => !q || v.name.toLowerCase().includes(q) || (v.city ?? '').toLowerCase().includes(q));
  }, [allVenues, sportFilter, cityFilter, searchQuery]);

  const resetFilters = () => {
    setSportFilter(ALL);
    setCityFilter(ALL);
    setSearchQuery('');
  };

  const filtersActive = sportFilter !== ALL || cityFilter !== ALL || searchQuery !== '';

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <MarketingNavbar />

      {/* Hero — dark so MarketingNavbar's transparent-white look stays readable above it. */}
      <section className="relative overflow-hidden bg-[#110D09] pt-28 pb-16 text-white">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(194,113,58,0.18),transparent_55%)]"
        />
        <div className="relative mx-auto max-w-6xl px-4 text-center">
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">Find a Venue</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-white/70 sm:text-base">
            Discover recreation facilities powered by RekPlay. Book golf simulators,
            pickleball courts, cricket nets, and more.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-neutral-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {sportOptions.map((opt) => (
                <button
                  key={opt.code}
                  type="button"
                  onClick={() => setSportFilter(opt.code)}
                  className={
                    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ' +
                    (sportFilter === opt.code
                      ? 'border-[#C2713A] bg-[#C2713A] text-white'
                      : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50')
                  }
                >
                  {opt.icon && <span aria-hidden="true">{opt.icon}</span>}
                  {opt.name}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="sr-only" htmlFor="city-filter">
                City
              </label>
              <select
                id="city-filter"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                className="rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]"
              >
                {cityOptions.map((c) => (
                  <option key={c} value={c}>
                    {c === ALL ? 'All cities' : c}
                  </option>
                ))}
              </select>

              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  placeholder="Search venues"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-md border border-neutral-200 bg-white py-2 pl-8 pr-3 text-sm text-neutral-700 placeholder:text-neutral-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] sm:w-56"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="mx-auto max-w-6xl px-4 py-10">
        {isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            Failed to load venues. Please try again in a moment.
          </div>
        )}

        {isLoading && <VenueCardSkeletonGrid />}

        {!isLoading && allVenues && allVenues.length === 0 && <EmptyPlatform />}

        {!isLoading && allVenues && allVenues.length > 0 && filteredVenues.length === 0 && (
          <NoResults onReset={resetFilters} />
        )}

        {!isLoading && filteredVenues.length > 0 && (
          <>
            <p className="mb-6 text-sm text-neutral-500">
              {filteredVenues.length} {filteredVenues.length === 1 ? 'venue' : 'venues'}
              {filtersActive ? ' match your filters' : ' available'}
            </p>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredVenues.map((venue) => (
                <VenueCard key={venue.id} venue={venue} />
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function VenueCard({ venue }: { venue: VenueSummary }) {
  const primarySport = venue.sports[0];
  const gradient = primarySport?.bgColor
    ? `linear-gradient(135deg, ${primarySport.bgColor}, #110D09 180%)`
    : 'linear-gradient(135deg, #C2713A, #110D09)';

  const priceLabel =
    venue.lowestStartingPrice != null && venue.currency
      ? `From $${Number(venue.lowestStartingPrice).toFixed(0)}/hr`
      : null;

  const visibleSports = venue.sports.slice(0, 3);
  const hiddenCount = venue.sports.length - visibleSports.length;

  return (
    <Link
      to={`/venue/${venue.slug}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white transition-all hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] focus-visible:ring-offset-2"
    >
      <div className="relative aspect-[16/9] w-full overflow-hidden" style={{ background: gradient }}>
        {venue.imageUrl ? (
          <img
            src={venue.imageUrl}
            alt={venue.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-4xl text-white/90">
            {primarySport?.icon ?? 'RekPlay'}
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">{venue.name}</h2>
          <p className="mt-1 flex items-center gap-1 text-sm text-neutral-500">
            <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
            {[venue.city, venue.province].filter(Boolean).join(', ') || 'Location TBA'}
          </p>
        </div>

        {visibleSports.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {visibleSports.map((s) => (
              <span
                key={s.sportCode}
                className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-xs font-medium text-neutral-700"
                style={s.color ? { color: s.color, borderColor: s.color + '33' } : undefined}
              >
                {s.icon && <span aria-hidden="true">{s.icon}</span>}
                {s.displayName}
              </span>
            ))}
            {hiddenCount > 0 && (
              <span className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-xs font-medium text-neutral-500">
                +{hiddenCount} more
              </span>
            )}
          </div>
        )}

        <div className="mt-auto flex items-center justify-between pt-2 text-sm text-neutral-600">
          <div className="flex items-center gap-3">
            {priceLabel && <span className="font-medium text-neutral-900">{priceLabel}</span>}
            {venue.totalUnits != null && venue.totalUnits > 0 && (
              <span className="flex items-center gap-1 text-neutral-500">
                <Users className="h-3.5 w-3.5" aria-hidden="true" />
                {venue.totalUnits} {venue.totalUnits === 1 ? 'unit' : 'units'}
              </span>
            )}
          </div>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-[#C2713A] transition-transform group-hover:translate-x-0.5">
            View venue
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function VenueCardSkeletonGrid() {
  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white"
        >
          <div className="aspect-[16/9] w-full animate-pulse bg-neutral-100" />
          <div className="space-y-3 p-5">
            <div className="h-5 w-2/3 animate-pulse rounded bg-neutral-100" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-neutral-100" />
            <div className="flex gap-2">
              <div className="h-6 w-16 animate-pulse rounded-full bg-neutral-100" />
              <div className="h-6 w-20 animate-pulse rounded-full bg-neutral-100" />
              <div className="h-6 w-14 animate-pulse rounded-full bg-neutral-100" />
            </div>
            <div className="h-4 w-1/3 animate-pulse rounded bg-neutral-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

function NoResults({ onReset }: { onReset: () => void }) {
  return (
    <div className="mx-auto max-w-md rounded-xl border border-neutral-200 bg-white p-10 text-center">
      <h2 className="text-lg font-semibold text-neutral-900">No venues found</h2>
      <p className="mt-2 text-sm text-neutral-500">
        Try adjusting your filters or search term to see more results.
      </p>
      <button
        type="button"
        onClick={onReset}
        className="mt-5 inline-flex items-center rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#a95f2f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] focus-visible:ring-offset-2"
      >
        Clear filters
      </button>
    </div>
  );
}

function EmptyPlatform() {
  return (
    <div className="mx-auto max-w-lg rounded-xl border border-neutral-200 bg-white p-10 text-center">
      <h2 className="text-lg font-semibold text-neutral-900">No venues yet</h2>
      <p className="mt-2 text-sm text-neutral-500">
        RekPlay is growing. Venues will appear here as operators join the platform.
      </p>
      <div className="mt-6 border-t border-neutral-100 pt-6">
        <p className="text-sm text-neutral-600">Are you a facility operator?</p>
        <Link
          to="/demo"
          className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#a95f2f]"
        >
          Book a demo
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}
